import {
    collection,
    CollectionReference,
    DocumentData,
    Timestamp,
    getCountFromServer,
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
    increment,
    arrayUnion,
    getDocs,
    query,
    where,
    limit,
    orderBy,
    DocumentSnapshot,
    startAfter,
    endBefore,
    limitToLast,
    startAt
} from "firebase/firestore";
import { DatabaseReference, ref, runTransaction, get } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { startAuthentication } from "@simplewebauthn/browser";
import { fs, db, functions } from "../firebase.ts";
import { getDeviceID } from "../components/global.ts";

export type User = {
    id: string;
    name: string;
    tip: number;
    pool: number;
    balance: number;
    isMention: boolean;
    isThanker: boolean;
};

export interface Moment {
    id?: string; // doc id => b.333.eco/m/id
    date?: Timestamp;
    user?: string;

    isSelf: boolean; // default = true // redundant with mentions.length but useful for querying
    isPrivate: boolean; // default = false
    gold: string; // thank you note
    jewel: string; // photo cover art // url to storage or stock // can be none => show avatar
    diamond: string; // url to video in storage
    type: "photo" | "short" | "none"; // can add more later

    mentions?: string[]; // uids mentioned in gold
    usernames?: string[]; // usernames mentioned in gold // needed to find in text
    // later: ai or company id
    hashtags?: string[];

    // user location
    lat: number;
    lng: number;

    // place location
    latitude: number;
    longitude: number;
    address: string; // human readable address
    place: string; // name of place
    group: "crew" | "org";
    gid?: string; // group id // TODO: later

    karat?: number; // int: number of words
    carat?: number; //video length; photo = 1; no photo = 0;
    kiitti?: number; // total int
    //
    kiitos?: number; // total int // always 0 initially

    duration?: number; // short length in minutes;
    minter?: { reward: number; account: number; fund: number; balance: number }; // not stored; for alert
    users?: User[]; // not stored; for alert
    aquaBalance?: number; // not stored; for alert // if hashtags
    fromBalances?: { kiitos: number; kiitti: number }; // not stored; for alert

    redates?: Timestamp[];
    rethankers?: string[]; // uid
    rementions?: string[]; // uid or hashtag
    ismentions?: boolean[];
    retokens?: string[];
    rekiitos?: number[]; // kiitos or kiitti
    iskiitos?: boolean[]; // kiitos or kiitti
    isthanks?: boolean[]; // to sender or to recipient

    tip?: number; // total cents // always 0 initially // receiver gratuity
    jar?: number; // total cents // always 0 initially // receiver donation
    kitty?: number; // total cents // always 0 initially // sender donation
    pool?: number; // total cents // always 0 initially // sender contribution
    retips?: number[]; // cents // always anonymous if not 0
    repools?: number[];

    // TODO: later add more fields for all stamp types: see Chests

    // stats: from here
    digs?: number; // total digs
    diggers?: string[]; // device id of diggers
    miners?: string[]; // uid of miners

    // stats: b app
    previews?: number;
    crawlers?: Array<string>;
    scans?: number; // total scans
    scanners?: string[]; // device id of scanners
}

type Response = {
    text: string;
    color: "green" | "red" | "orange"; // status
    data?: Moment;
};

//TODO: later allow tell aqua to thank someone? pro not bc can just thank directly

export class Kiitti {
    private rtdb: DatabaseReference;
    private collection: CollectionReference<DocumentData>;

    constructor() {
        this.rtdb = ref(db, "kiitti"); //TODO: later change to moments
        this.collection = collection(fs, "kiitti");
    }

    async getKiitti(): Promise<number> {
        try {
            const snapshot = await get(this.rtdb);
            if (snapshot.exists()) {
                return snapshot.val() as number;
            } else {
                //console.log("No data available");
                return 0; // Return 0 if the counter doesn't exist yet
            }
        } catch (error) {
            console.error("Error fetching Kiitti Balance:", error);
            throw error;
        }
    }

    // not used on client
    async incrementKiitti(kiitti: number): Promise<number> {
        try {
            const result = await runTransaction(
                this.rtdb,
                (currentValue: number | null) => {
                    // If the data doesn't exist yet, initialize it to 1
                    if (currentValue === null) {
                        return kiitti;
                    }

                    // Otherwise, increment the current value
                    return currentValue + kiitti;
                }
            );

            if (result.committed) {
                console.log(`New Kiitti Balance: ${result.snapshot.val()}`);
                return result.snapshot.val();
            } else {
                throw new Error("Transaction not committed.");
            }
        } catch (error) {
            console.error("Increment failed: ", error);
            throw error;
        }
    }

    async count(): Promise<number> {
        const snapshot = await getCountFromServer(this.collection);
        return snapshot.data().count;
    }

    async get(
        id: string
    ): Promise<{ data: Moment | null; cursor: DocumentSnapshot | null }> {
        const docRef = doc(this.collection, id); // Ensure this points to "kiitti"
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                data: { id: docSnap.id, ...docSnap.data() } as Moment,
                cursor: docSnap // The actual snapshot used for startAfter/endBefore
            };
        }

        return { data: null, cursor: null };
    }

    // deprecated
    async get2(id: string): Promise<Moment | null> {
        const docRef = doc(fs, "kiitti", id);
        const docSnap = await getDoc(docRef);
        return docSnap.exists()
            ? ({ id: docSnap.id, ...docSnap.data() } as Moment)
            : null;
    }

    // not used: redundant with getRock()
    // public feed: history
    async getLatest(uid: string): Promise<Moment | null> {
        const q = uid
            ? query(
                  this.collection,
                  where("user", "==", uid),
                  orderBy("date", "desc"),
                  limit(1)
              )
            : query(
                  this.collection,
                  where("isPrivate", "==", false),
                  orderBy("date", "desc"),
                  limit(1)
              );

        const snap = await getDocs(q);
        if (!snap.empty) {
            const docSnap = snap.docs[0];
            return { id: docSnap.id, ...docSnap.data() } as Moment;
        }

        return null;
    }

    // public feed: boost
    async getRandom(uid: string): Promise<Moment | null> {
        // Create a dummy document reference to get a random ID string
        const autoId = doc(this.collection).id;

        // Query for the first document whose ID is "greater than" our random ID
        const q = uid
            ? query(
                  this.collection,
                  where("user", "==", uid),
                  orderBy("__name__"), // __name__ refers to the Document ID
                  startAt(autoId),
                  limit(1)
              )
            : query(
                  this.collection,
                  where("isPrivate", "==", false),
                  orderBy("__name__"), // __name__ refers to the Document ID
                  startAt(autoId),
                  limit(1)
              );

        let snap = await getDocs(q);

        // Wrap around if we hit the end of the ID list
        if (snap.empty) {
            const wrapQuery = uid
                ? query(
                      this.collection,
                      where("user", "==", uid),
                      orderBy("__name__"),
                      limit(1)
                  )
                : query(
                      this.collection,
                      where("isPrivate", "==", false),
                      orderBy("__name__"),
                      limit(1)
                  );
            snap = await getDocs(wrapQuery);
        }

        if (!snap.empty) {
            const docSnap = snap.docs[0];
            return { id: docSnap.id, ...docSnap.data() } as Moment;
        }

        return null;
    }

    // map
    async getAll(uid: string): Promise<Moment[]> {
        const q = uid
            ? query(
                  this.collection,
                  where("user", "==", uid),
                  orderBy("date", "desc"),
                  limit(10)
              )
            : query(
                  this.collection,
                  where("isPrivate", "==", false),
                  orderBy("date", "desc"),
                  limit(10)
              );

        const snaps = await getDocs(q);
        return snaps.docs.reverse().map(
            (docSnap) =>
                ({
                    id: docSnap.id,
                    ...docSnap.data()
                }) as Moment
        );
    }

    // b-journal
    async getRock(
        uid?: string,
        currentCursor: DocumentSnapshot | null = null,
        direction: "next" | "prev" = "next"
    ): Promise<{ data: Moment | null; cursor: DocumentSnapshot | null }> {
        // 1. Determine base filters
        const constraints = uid
            ? [where("user", "==", uid)]
            : [where("isPrivate", "==", false)];

        // 2. Build the query with direction logic
        let q = query(this.collection, ...constraints, orderBy("date", "desc"));

        if (currentCursor) {
            if (direction === "next") {
                // 'next' goes further back in time (descending order)
                q = query(q, startAfter(currentCursor), limit(1));
            } else {
                // 'prev' goes toward more recent items
                q = query(q, endBefore(currentCursor), limitToLast(1));
            }
        } else {
            q = query(q, limit(1));
        }

        const snap = await getDocs(q);

        if (!snap.empty) {
            const docSnap = snap.docs[0];
            return {
                data: { id: docSnap.id, ...docSnap.data() } as Moment,
                cursor: docSnap
            };
        }

        return { data: null, cursor: null };
    }

    // deprecated
    async getRock2(uid: string): Promise<Moment | null> {
        const q = uid
            ? query(
                  this.collection,
                  where("user", "==", uid),
                  orderBy("date", "desc"),
                  limit(1)
              )
            : query(
                  this.collection,
                  where("isPrivate", "==", false),
                  orderBy("date", "desc"),
                  limit(1)
              );

        const snap = await getDocs(q);
        if (!snap.empty) {
            const docSnap = snap.docs[0];
            return { id: docSnap.id, ...docSnap.data() } as Moment;
        }

        return null;
    }

    async capture(
        isQuest: boolean,
        isSelf: boolean,
        isPrivate: boolean,

        gold: string,
        jewel: string,
        diamond: string,
        duration: number,
        type: Moment["type"],

        lat: number,
        lng: number,

        latitude: number,
        longitude: number,
        address: string,
        place: string,
        group: Moment["group"]
    ): Promise<Response> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "thank/capture" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "thank/capture",

                    isQuest,
                    isSelf,
                    isPrivate,

                    gold,
                    jewel,
                    diamond,
                    duration,
                    type,

                    lat,
                    lng,
                    latitude,
                    longitude,
                    address,
                    place,
                    group
                })
            ).data as Moment;

            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async rethank(
        uid: string, // not used for now
        id: string,
        mention: string,
        hashtag: string,
        token: string,
        tip: number
    ): Promise<Response> {
        localStorage.setItem("tip", tip.toString());

        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "thank/recapture" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "thank/recapture",
                    id,
                    mention,
                    hashtag,
                    token,
                    tip
                })
            ).data as Moment;

            localStorage.removeItem("tip");
            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("tip");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async addMiner(uid: string, id: string) {
        const docRef = doc(fs, "kiitti", id);
        uid
            ? await updateDoc(docRef, {
                  digs: increment(1),
                  miners: arrayUnion(uid)
              })
            : await updateDoc(docRef, {
                  digs: increment(1),
                  diggers: arrayUnion(getDeviceID())
              });
    }

    // not allowed bc blockchain
    async remove(id: string) {
        const docRef = doc(fs, "kiitti", id);
        await deleteDoc(docRef);
    }
}
